import React, { useCallback, useState } from 'react';
import { required, useTranslate } from 'react-admin';
import { Form, Field } from 'react-final-form';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Box, Button, Grid, Tooltip, useTheme } from '@material-ui/core';
import moment from 'moment';
import axios from 'axios';
import { useStyles } from '../../../../base/components/guesser/report/search-panel';
import GroupSelector from '../../../../base/components/customized/common-input/GroupSelector';
import { useAuthUser } from '../../../../base/hooks';
import DateRangeInput from '../../../../base/components/guesser/date-range-input';
import ExportButton from './ExportButton';
import { sanitizeObject } from '../../../../services/util';
import { useAccountInvoiceBalanceListContext } from './AccountInvoiceBalanceProvider';
import { parseServerDate } from '../../../../services/util/date';

const AccountInvoiceBalanceFilters = () => {
  const theme = useTheme();
  const t = useTranslate();
  const classes = useStyles();
  const history = useHistory();
  const { pathname } = useLocation();
  const { data, query } = useAccountInvoiceBalanceListContext();
  const [groupOpeningBalanceTime, setGroupOpeningBalanceTime] = useState();

  const handleFilter = useCallback(
    (formValues) => {
      const params = queryString.stringify(
        sanitizeObject({
          groupId: formValues.groupId,
          time: formValues.time,
        }),
        {
          arrayFormat: 'index',
        },
      );

      history.push({
        pathname,
        search: params,
      });
    },
    [pathname],
  );

  const groupValidate = useCallback(
    async (groupId) => {
      if (!groupId) {
        return t('ra.validation.required');
      }

      const groupData = await axios.get(`/api/group/${groupId}`);

      if (!groupData?.data?.openingBalanceTime) {
        return t('resources.accountInvoiceBalance.validation.noGroupOpeningBalance');
      }

      setGroupOpeningBalanceTime(groupData.data.openingBalanceTime);

      return undefined;
    },
    [t],
  );

  return (
    <Form
      initialValues={query}
      onSubmit={handleFilter}
      render={({ handleSubmit, dirty, form, values }) => {
        const hasNoData = !data?.items?.length;
        const hasNoValidGroup = !values.groupId || !groupOpeningBalanceTime;

        return (
          <Box
            component="form"
            className={classes.formStyle}
            width="100%"
            display="flex"
            flexDirection="column"
            onSubmit={handleSubmit}
          >
            <Grid container spacing={0.5} display="flex">
              <Field name="groupId">
                {({ meta }) => (
                  <div>
                    <GroupFilter
                      onChange={() => {
                        setGroupOpeningBalanceTime(undefined);
                        form.change('time', []);
                      }}
                      validate={groupValidate}
                    />
                    <Box
                      sx={{
                        marginLeft: '14px',
                        color: theme.palette.error.main,
                        minHeight: '20px',
                        fontSize: '0.75rem',
                      }}
                    >
                      {meta.error && meta.touched ? meta.error : undefined}
                    </Box>
                  </div>
                )}
              </Field>
              <Tooltip title={hasNoValidGroup ? t('resources.accountInvoiceBalance.selectGroupFirst') : ''}>
                <Box display="inline-block">
                  <Field name="time">
                    {({ meta }) => (
                      <div>
                        <DateRangeInput
                          source="time"
                          label={t('resources.accountInvoiceBalance.fields.statementPeriod')}
                          defaultRangeOption="date"
                          hasRangeOption={false}
                          showTime={false}
                          allowClear={[true, true]}
                          allowEmpty={[false, true]}
                          ranges={[]}
                          disabled={hasNoValidGroup}
                          serverFormat={null}
                          validate={required(t('ra.validation.required'))}
                          disabledDate={(current) =>
                            !!current &&
                            !!groupOpeningBalanceTime &&
                            moment(current).isBefore(parseServerDate(groupOpeningBalanceTime).startOf('month'))
                          }
                        />
                        <Box
                          sx={{
                            margin: '4px 0px 0px 14px',
                            color: theme.palette.error.main,
                            minHeight: '20px',
                            fontSize: '0.75rem',
                          }}
                        >
                          {meta.error && meta.touched ? meta.error : undefined}
                        </Box>
                      </div>
                    )}
                  </Field>
                </Box>
              </Tooltip>
            </Grid>

            <Box display="flex" alignItems="center" mb={3} mt={1}>
              <Button type="submit" disabled={!dirty} className={`${classes.btn} search-panel__export-button`}>
                {t('ra.action.search')}
              </Button>
              <ExportButton disabled={hasNoData} />
            </Box>
          </Box>
        );
      }}
    />
  );
};

export default AccountInvoiceBalanceFilters;

const GroupFilter = ({ error, ...props }) => {
  const user = useAuthUser();
  const userGroupId = user.group?.id;

  return (
    <GroupSelector
      source="groupId"
      defaultValue={userGroupId}
      disabled={Boolean(userGroupId)}
      error={error}
      {...props}
    />
  );
};
